import * as Sentry from '@sentry/react';
import { HomeyAPIV3Local } from 'homey-api';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import { SCOPES } from '../lib/scopes';
import { ToastManager } from '../ToastManager';

import { BaseStore } from './BaseStore';
import { HomeyUtils } from './HomeyUtils';

import { authStore } from './AuthStore';

export function getLocalBaseUrl() {
  return import.meta.env.VITE_HOMEY_LOCAL_ADDRESS ?? window.location.origin;
}

export class SelectedHomeyStore extends BaseStore {
  createInitialState() {
    return {
      selectedHomey: null,
      baseUrl: null,
      apiSilent: false,
      api: null,
      loading: true,
      error: null,
      isConnected: null,
      isRebooting: null,
      isUpdating: null,
      bootId: null,
      scopes: {},
      language: null,
    };
  }

  store = createWithEqualityFn(
    devtools(
      subscribeWithSelector((set, get, api) => ({
        ...this.createInitialState(),
      })),
      { name: 'homey' }
    ),
    shallow
  );

  retry() {
    // Pass null on sh?
    this.authenticateHomey(this.get().selectedHomey).catch(console.error);
  }

  reset() {
    this.destroyApi();
    this.set(this.createInitialState());
  }

  async createApi(homey) {
    if (import.meta.env.MODE === 'self-hosted') {
      const token = sessionStorage.getItem('HOMEY_LOCAL_TOKEN');

      const props = {
        token,
        // debug: console.log,
        baseUrl: getLocalBaseUrl(),
        strategy: [],
        reconnect: false,
        properties: {
          id: homey.id,
          softwareVersion: homey.softwareVersion,
        },
      };

      const api = new HomeyAPIV3Local(props);

      const session = await api.sessions
        .getSessionMe({
          $socket: false,
          shouldRetry: false,
        })
        .catch((error) => {
          if (error.statusCode === 401) {
            sessionStorage.removeItem('HOMEY_LOCAL_TOKEN');
            this.abortController.abort();
            authStore.logout();
          }

          throw error;
        });
      api.__session = session;

      return api;
    }

    // 'localSecure'|'local'|'cloud'|'mdns'
    // const api = await homey.authenticate({ strategy: ['cloud'] });
    // const api = await homey.authenticate({
    //   reconnect: false,
    // });
    const api = await homey.authenticate({
      reconnect: false,
    });
    return api;
  }

  async authenticateHomey(homey) {
    this.destroyApi();

    if (this.abortController != null) {
      this.abortController.abort();
    }

    const controller = new AbortController();
    const signal = controller.signal;
    this.abortController = controller;

    if (homey == null) {
      const res = await fetch(`${getLocalBaseUrl()}/api/manager/system/ping`);

      if (res.headers.has('X-Homey-ID') === false) {
        throw new Error(`No Homey Found At Address: ${getLocalBaseUrl()}`);
      }

      if (res.headers.has('X-Homey-Version') === false) {
        throw new Error(`No Homey Version Found At Address: ${getLocalBaseUrl()}`);
      }

      homey = {
        id: res.headers.get('X-Homey-ID'),
        licenses: [],
        platform: 'local',
        platformVersion: 2,
        apiVersion: 3,
        softwareVersion: res.headers.get('X-Homey-Version'),
      };
      // return;
    }

    try {
      this.set({
        loading: true,
        error: null,
        isConnected: false,
        isRebooting: null,
        isUpdating: null,
        bootId: null,
        scopes: {},
        language: homey.language,
      });

      const api = await this.createApi(homey);

      if (signal.aborted) {
        return;
      }

      const { baseUrl, scopes } = await this.onInitApi({ signal, api, homey });

      if (signal.aborted) {
        return;
      }

      this.set({
        selectedHomey: homey,
        api,
        apiSilent: false,
        scopes,
        baseUrl,
        loading: false,
        error: null,
        isConnected: true,
        isRebooting: null,
        isUpdating: null,
      });
    } catch (error) {
      // If the Homey was offline and another one was selected during loading we would never hit the
      // signal aborted because authenticate would throw. So we check the signal again here and
      // don't set the state after an error if the authenticateHomey was aborted.
      if (signal.aborted) {
        return;
      }

      this.set({
        // Leave selectedHomey because we might have to retry after an error.
        selectedHomey: homey,
        baseUrl: null,
        api: null,
        apiSilent: false,
        scopes: {},
        loading: false,
        error,
        isConnected: null,
        isRebooting: null,
        isUpdating: null,
      });
    }
  }

  async onInitApi({ signal, api, homey }) {
    // const cloudUser = authStore.get().cloudUser;

    api.homey = homey;
    api.homeyVersion = homey.softwareVersion?.split?.('-')[0];

    api.isCloud = api.homey.platform === 'cloud';
    api.isCloudPremium = false;
    api.isCloudAndPremium = false;

    let hasAdvancedFlowLicence = HomeyUtils.getHasAdvancedFlowLicense(api.homey);
    let hasAdvancedFlowVersion = HomeyUtils.getHasAdvancedFlowVersion(api.homey);
    const useSeamlessDevicePairingScreens = HomeyUtils.getIsSeamlessDevicePairingScreens(api.homey);

    // We need to do some http request for cloud to fill the tier cache.
    //
    // TODO
    // Fix this in athom-cloud-api or something instead of based on headers.
    if (api.isCloud === true) {
      await api.system.ping();
      api.isCloudPremium = api.tier === 'premium';
      api.isCloudAndPremium = api.isCloud && api.isCloudPremium;

      if (api.isCloud === true) {
        hasAdvancedFlowVersion = true;
      }

      if (api.isCloudAndPremium === true) {
        hasAdvancedFlowLicence = true;
      }

      if (signal.aborted) {
        return;
      }
    }

    api.features = {
      advancedFlow: {
        get hasLicense() {
          if (api.features.advancedFlow.hasLicenseOverride === true) {
            return true;
          }

          return hasAdvancedFlowLicence;
        },
        hasVersion: hasAdvancedFlowVersion,
      },
      moods: {
        hasMoods: HomeyUtils.getHasMoods(api.homey),
      },
      useSeamlessDevicePairingScreens,
    };

    await api.connect();

    api.on('disconnect', (...args) => {
      console.log('Homey disconnected', ...args);
      this.reconnect({ homey, signal });
    });

    const baseUrl = await api.baseUrl;

    // If the language wasnt synced properly to cloud. Most of the time these would be
    // the same but only when changing the Homey language the sync might not have been
    // done yet.
    api.i18n
      .getOptionLanguage()
      .then((result) => {
        if (signal.aborted) {
          return;
        }

        if (this.get().language !== result.value) {
          this.set({
            language: result.value,
          });
        }
      })
      .catch((error) => {
        if (signal.aborted) {
          return;
        }

        ToastManager.handleError(error);
      });

    // Lazy update the bootId.
    api.system
      .getInfo()
      .then((info) => {
        if (signal.aborted) {
          return;
        }

        this.get().bootId = info.bootId;
      })
      .catch((error) => {
        if (signal.aborted) {
          return;
        }

        console.error(error);
      });

    const scopes = {};

    for (const [, SCOPE] of Object.entries(SCOPES)) {
      scopes[SCOPE] = api.hasScope(SCOPE);
    }

    Sentry.setContext('homey', {
      id: api.homey.id,
      softwareVersion: api.homey.softwareVersion,
      apiVersion: api.homey.apiVersion,
      platform: api.homey.platform,
      platformVersion: api.homey.platformVersion,
      model: api.homey.model,
      name: api.homey.name,
      region: api.homey.region,
    });

    Sentry.setTag('softwareVersion', api.homey.softwareVersion);
    Sentry.setTag('apiVersion', api.homey.apiVersion);
    Sentry.setTag('platform', api.homey.platform);
    Sentry.setTag('platformVersion', api.homey.platformVersion);

    document.title = homey?.name ?? 'My Homey';

    return {
      baseUrl,
      scopes,
    };
  }

  reconnect({ signal, homey }) {
    if (signal.aborted === true) {
      return;
    }

    setTimeout(async () => {
      try {
        if (signal.aborted === true) {
          return;
        }

        const api = await this.createApi(homey);

        if (signal.aborted === true) {
          return;
        }

        const { baseUrl, scopes } = await this.onInitApi({ signal, api, homey });
        this.destroyApi();
        this.set({
          selectedHomey: homey,
          api,
          apiSilent: true,
          scopes,
          baseUrl,
          loading: false,
          error: null,
          isConnected: true,
          isRebooting: null,
          isUpdating: null,
        });
      } catch (error) {
        if (signal.aborted === true) {
          return;
        }

        if (error.message === 'Subscription Inactive') {
          this.set({
            // Leave selectedHomey because we might have to retry after an error.
            selectedHomey: homey,
            baseUrl: null,
            api: null,
            apiSilent: false,
            scopes: {},
            loading: false,
            error,
            isConnected: null,
            isRebooting: null,
            isUpdating: null,
          });
          return;
        }

        console.log('Reconnect failed', error);

        if (error.message === 'Subscription Inactive') {
          this.set({
            // Leave selectedHomey because we might have to retry after an error.
            selectedHomey: homey,
            baseUrl: null,
            api: null,
            apiSilent: false,
            scopes: {},
            loading: false,
            error,
            isConnected: null,
            isRebooting: null,
            isUpdating: null,
          });

          return;
        }

        this.reconnect({ signal, homey });
      }
    }, 5000);
  }

  destroyApi() {
    const state = selectedHomeyStore.get();

    if (state.api != null) {
      state.api.destroy();
    }
  }

  assignLicences({ licenses }) {
    this.get().selectedHomey.licenses = licenses;
  }

  assignAdvancedFlowLicense() {
    const { api } = this.get();

    // This whole approach is a bit odd since we assume something will rerender to reflect the new
    // state. For now it's ok and better than refreshing the whole api (since that will refetch everything).
    if (api != null) {
      api.features.advancedFlow.hasLicenseOverride = true;
    }
  }

  async reboot() {
    const { api } = this.get();

    if (api != null) {
      api.removeAllListeners('disconnect');
      const { bootId } = await api.system.reboot();

      this.set({
        isRebooting: true,
        bootId,
      });
    }
  }

  awaitReboot({ bootId }) {
    const { api } = this.get();

    if (api != null) {
      api.removeAllListeners('disconnect');
      this.set({
        isRebooting: true,
        bootId,
      });
    }
  }

  setIsUpdating(value) {
    this.set({
      isUpdating: value,
    });
  }

  getApi() {
    const state = this.get();
    return state.api;
  }
}

export const selectedHomeyStore = new SelectedHomeyStore();

function selector(state) {
  return {
    selectedHomey: state.selectedHomey,
    baseUrl: state.baseUrl,
    api: state.api,
    loading: state.loading,
    error: state.error,
    retry: state.retry,
    isConnected: state.isConnected,
    isRebooting: state.isRebooting,
    isUpdating: state.isUpdating,
    bootId: state.bootId,
  };
}

function selectApiAndScopes(state) {
  return { api: state.api, scopes: state.scopes };
}

function selectBaseUrl(state) {
  return state.baseUrl;
}

function selectSelectedHomey(state) {
  return state.selectedHomey;
}

function selectLanguage(state) {
  return state.language;
}

export function useSelectedHomeyState() {
  return selectedHomeyStore.store(selector, shallow);
}

export function useApi() {
  return selectedHomeyStore.store(selectApiAndScopes);
}

export function useBaseUrl() {
  return { baseUrl: selectedHomeyStore.store(selectBaseUrl) };
}

export function useSelectedHomey() {
  return selectedHomeyStore.store(selectSelectedHomey);
}

export function useSelectedHomeyLanguage() {
  return selectedHomeyStore.store(selectLanguage, shallow);
}
